/**
 * Padrão de formulário (memória dfe-padrao-formulario): todo campo é
 * `label.field` com `.rot` em cima e controle de 38px; larguras só por
 * `className="cN"` dentro de `.fgrid`. Nunca `style={{flex}}`.
 */
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { mascaraCompetencia } from '../lib/formato';

interface Base {
  rotulo: ReactNode;
  className?: string;
  ajuda?: ReactNode;
  obrigatorio?: boolean;
  erro?: string | null;
}

function cls(base: string, extra?: string, erro?: string | null) {
  return [base, extra, erro ? 'erro' : null].filter(Boolean).join(' ');
}

export type Opcao = readonly [valor: string | number, texto: string];

export function Sel({ rotulo, valor, opcoes, onChange, className, ajuda, obrigatorio, erro, vazio, ...rest }:
  Base & { valor: string | number | null | undefined; opcoes: readonly Opcao[]; onChange: (v: string) => void; vazio?: string } & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange' | 'className'>) {
  return (
    <label className={cls('field', className, erro)}>
      <span className="rot">{rotulo}{obrigatorio ? ' *' : ''}</span>
      <select value={String(valor ?? '')} onChange={(e) => onChange(e.target.value)} {...rest}>
        {vazio !== undefined && <option value="">{vazio}</option>}
        {opcoes.map(([v, t]) => <option key={String(v)} value={String(v)}>{t}</option>)}
      </select>
      {erro ? <span className="msg-erro">{erro}</span> : ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

export function Campo({ rotulo, valor, onChange, className, ajuda, obrigatorio, erro, alinhar, ...rest }:
  Base & { valor: string | null | undefined; onChange: (v: string) => void; alinhar?: 'dir' } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'className'>) {
  return (
    <label className={cls('field', className, erro)}>
      <span className="rot">{rotulo}{obrigatorio ? ' *' : ''}</span>
      <input type="text" value={valor ?? ''} onChange={(e) => onChange(e.target.value)} className={rest.readOnly ? 'ro' : undefined}
        style={alinhar === 'dir' ? { textAlign: 'right' } : undefined} {...rest} />
      {erro ? <span className="msg-erro">{erro}</span> : ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

export function Area({ rotulo, valor, onChange, className, ajuda, obrigatorio, erro, linhas = 3, placeholder }:
  Base & { valor: string | null | undefined; onChange: (v: string) => void; linhas?: number; placeholder?: string }) {
  return (
    <label className={cls('field', className, erro)}>
      <span className="rot">{rotulo}{obrigatorio ? ' *' : ''}</span>
      <textarea value={valor ?? ''} onChange={(e) => onChange(e.target.value)} rows={linhas} placeholder={placeholder} />
      {erro ? <span className="msg-erro">{erro}</span> : ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

export function Check({ rotulo, valor, onChange, className, ajuda }:
  { rotulo: ReactNode; valor: boolean; onChange: (v: boolean) => void; className?: string; ajuda?: ReactNode }) {
  return (
    <label className={cls('field check', className)}>
      <input type="checkbox" checked={valor} onChange={(e) => onChange(e.target.checked)} />
      <span>{rotulo}</span>
      {ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

/** "1" vira 1,00; "1.500" vira 1.500,00; "0.65" vira 0,65. */
export function numeroDe(v: string | number | null | undefined): number | null {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return v;
  let s = String(v).trim().replace(/\s|R\$|%/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
export const fmtNumero = (v: string | number | null | undefined, casas = 2): string => {
  const n = numeroDe(v);
  return n === null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
};

export function CampoMoeda({ rotulo, valor, onChange, className, ajuda, obrigatorio, erro, readOnly, placeholder = '0,00' }:
  Base & { valor: string | null | undefined; onChange: (v: string) => void; readOnly?: boolean; placeholder?: string }) {
  return (
    <label className={cls('field', className, erro)}>
      <span className="rot">{rotulo}{obrigatorio ? ' *' : ''}</span>
      <div className={`igrp${readOnly ? ' ro' : ''}`}>
        <span>R$</span>
        <input value={valor ?? ''} onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, ''))} onBlur={() => !readOnly && onChange(fmtNumero(valor))}
          placeholder={placeholder} inputMode="decimal" readOnly={readOnly} />
      </div>
      {erro ? <span className="msg-erro">{erro}</span> : ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

export function Pct({ rotulo, valor, onChange, className, ajuda, casas = 2, readOnly, erro }:
  Base & { valor: string | null | undefined; onChange: (v: string) => void; casas?: number; readOnly?: boolean }) {
  return (
    <label className={cls('field', className, erro)}>
      <span className="rot">{rotulo}</span>
      <div className={`igrp${readOnly ? ' ro' : ''}`}>
        <input value={valor ?? ''} onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, ''))} onBlur={() => onChange(fmtNumero(valor, casas))}
          placeholder="0,00" inputMode="decimal" readOnly={readOnly} />
        <span className="suf">%</span>
      </div>
      {ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

export function CampoNumero({ rotulo, valor, onChange, className, ajuda, obrigatorio, erro, sufixo, casas, placeholder = '0', min, max }:
  Base & { valor: string | number | null | undefined; onChange: (v: string) => void; sufixo?: string; casas?: number; placeholder?: string; min?: number; max?: number }) {
  return (
    <label className={cls('field', className, erro)}>
      <span className="rot">{rotulo}{obrigatorio ? ' *' : ''}</span>
      <div className="igrp">
        <input value={valor ?? ''} onChange={(e) => onChange(e.target.value.replace(/[^\d.,-]/g, ''))}
          onBlur={() => casas != null && valor !== '' && onChange(fmtNumero(valor, casas))} placeholder={placeholder} inputMode="decimal" min={min} max={max} />
        {sufixo && <span className="suf">{sufixo}</span>}
      </div>
      {erro ? <span className="msg-erro">{erro}</span> : ajuda && <span className="ajuda">{ajuda}</span>}
    </label>
  );
}

/** Campo de competência (mm/aaaa) com máscara: digitar 092026 ou 202609 vira 09/2026. */
export function CampoCompetencia({ onChange, placeholder = 'mm/aaaa', ...rest }: Omit<Parameters<typeof Campo>[0], 'onChange'> & { onChange: (v: string) => void }) {
  return <Campo {...rest} placeholder={placeholder} inputMode="numeric" maxLength={7} onChange={(v) => onChange(mascaraCompetencia(v))} />;
}
